<script setup>
  import { useSlots } from 'vue';

  const props = defineProps({
    color: {
      type: String,
      required: false,
      default: 'danger',
      validator(value) {
        return ['danger', 'success', 'information'].includes(value);
      },
    },
    prefix: {
      type: String,
      required: false,
      default: 'Algo deu errado!',
    },
    // Drops the block's bottom margin so the alert sits flush inside a tight
    // container (e.g. a thread bubble) that owns its own spacing.
    marginless: {
      type: Boolean,
      required: false,
      default: false,
    },
    // WHERE THE ALERT SITS in what surrounds it, which is what decides on which
    // side it needs air:
    //
    //   * `top`    - margin below (the default: an alert that opens a block)
    //   * `middle` - margin above and below
    //   * `bottom` - margin above, nothing below (an alert that closes a block)
    //
    // `marginless` is a different axis and WINS over this one: it means "no
    // margin at all", the more specific instruction ("the container owns the
    // spacing"). The stylesheet spells out the rule order that enforces it.
    position: {
      type: String,
      required: false,
      default: 'top',
      validator(value) {
        return ['top', 'middle', 'bottom'].includes(value);
      },
    },
  });

  const classes = ['credere-alert', `color--${props.color}`, `position--${props.position}`]
    .concat(props.marginless ? ['variant--marginless'] : []);
  const slots = useSlots();
</script>

<template>
  <section :class="classes">
    <span class="credere-alert-icon">!</span>

    <div class="credere-alert-paragraph">
      <slot>
        <p>
          <b class="credere-alert-prefix">
            {{ prefix }}
          </b>

          Por favor, verifique os campos preenchidos e tente novamente.

          <span
            v-if="slots.extra"
            class="credere-alert-extra"
          >
            <slot name="extra" />
          </span>
        </p>
      </slot>
    </div>
  </section>
</template>

<style lang="scss">
  @import '@css/mixins/mobile.scss';

  .credere-alert {
    --background-color: var(--token-color-constant-dark);
    --border-color: var(--token-color-alert-danger);
    --border-style: solid;
    --border-width: 0 0 0 var(--token-spacing-xxxs);
    --border-radius: var(--token-border-radius-small);
    --color: var(--token-color-constant-light);
    --font-size: var(--token-font-size-m);
    --gap: var(--token-spacing-xxxs);
    --line-height: var(--token-line-height-l);
    --margin: 0 0 var(--token-spacing-blaster) 0;
    --padding: var(--token-spacing-xxs);

    align-items: flex-start;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    @include mobile-font-size;

    // `position--top` has no block on purpose: it IS the --margin declared
    // above, so the default keeps behaving exactly as it did before the prop
    // existed (the same idiom Buttons uses for `spacing--none`).
    &.position--middle {
      --margin: var(--token-spacing-blaster) 0;
    }

    &.position--bottom {
      --margin: var(--token-spacing-blaster) 0 0 0;
    }

    // LAST ON PURPOSE, AND IT HAS TO STAY LAST: "no margin at all" beats any
    // position, and both selectors weigh the same, so source order is what
    // decides. Moving this block above the ones over it silently un-does
    // `marginless` for anyone who also passes `position`.
    &.variant--marginless {
      --margin: 0;
    }

    &.color--success {
      --border-color: var(--token-color-alert-success);
    }

    &.color--information {
      --border-color: var(--token-color-alert-information);
    }

    b {
      --font-weight: var(--token-font-weight-bold);
    }
  }

  .credere-alert-icon {
    --background-color: var(--token-color-alert-danger);
    --border-radius: var(--token-border-radius-circular);
    --color: var(--token-color-constant-dark);
    --font-size: var(--token-font-size-s);
    --font-weight: var(--token-font-weight-bold);
    --margin: 0.25rem 0 0;

    flex-shrink: 0;
    height: 1rem;
    line-height: 1rem;
    text-align: center;
    width: 1rem;

    .color--success & {
      --background-color: var(--token-color-alert-success);
    }

    .color--information & {
      --background-color: var(--token-color-alert-information);
    }
  }

  .credere-alert-prefix {
    --font-weight: var(--token-font-weight-bold);
  }

  .credere-alert-paragraph {
    display: block;
  }

  .credere-alert-extra {
    --font-size: var(--token-font-size-s);
    --margin: var(--token-spacing-s) 0 0;

    display: block;
  }
</style>
